import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import socketService from './SocketService';
import { getStoredUser } from '../../../Config/ApiConfig';
import { useGlobal } from '../GlobalContext/GlobalContext';

export const SocketContext = createContext(null);

// Point to the backend server where Socket.IO is running
const serverUrl = "https://custom-answer.com/";

export function SocketProvider({ children }) {
  const { token } = useGlobal();
  const [connectionState, setConnectionState] = useState({
    status: 'disconnected',
    error: null
  });
  const isInitialized = useRef(false);

  // Handler for connection state changes
  const handleConnectionChange = useCallback((state) => {
    setConnectionState(prevState => {
      const newState = {
        ...prevState,
        status: state.status,
        error: state.status === 'error' ? state.message : null
      };
      return newState;
    });
  }, [setConnectionState]);

  // Handler for socket errors
  const handleError = useCallback((error) => {
    setConnectionState(prevState => ({
      ...prevState,
      error: error.message || 'Unknown socket error'
    }));
  }, []);

  // Initialize socket service
  useEffect(() => {
    if (!token || !serverUrl) {
      return;
    }

    // Set up listeners for connection changes and errors
    const unsubscribeConnection = socketService.onConnect(handleConnectionChange);
    const unsubscribeError = socketService.onError(handleError);

    // Initialize and connect
    socketService.initialize(serverUrl, token.replace('Bearer ', ''));
    socketService.connect();

    return () => {
      unsubscribeConnection();
      unsubscribeError();
      // Use softDisconnect here so that token changes or
      // temporary unmounts do not wipe subscription state.
      socketService.softDisconnect();
    };
  }, [handleConnectionChange, handleError, token]);

  // Update socket authentication when token changes
  useEffect(() => {
    if (token && isInitialized.current) {
      socketService.updateToken(token.replace('Bearer ', ''));
    }

    if (token) {
      isInitialized.current = true;
    }
  }, [token]);

  const contextValue = {
    socket: socketService,
    connectionState
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
}
